import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { authMe, authLogin, authRegister, authLogout, authApple, authGoogle, syncDecisions, type AuthUser } from './api'
import { useStore } from './store'
import { toast } from './components/Toast'
import { analytics } from './lib/analytics'
import type { Decision } from './types'

interface AuthCtx {
  user: AuthUser | null | undefined // undefined = loading
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, name?: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  appleSignIn: () => Promise<void>
  googleSignIn: (idToken: string) => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

/** Read decisions snapshot directly from the zustand store outside React. */
function getLocalDecisions(): Decision[] {
  try {
    return useStore.getState().decisions
  } catch {
    return []
  }
}

/** Replace store decisions with `merged`. Preserves existing results map. */
function setLocalDecisions(merged: Decision[]): void {
  useStore.setState((s: any) => ({ ...s, decisions: merged }))
}

const ACTIVE_UID_KEY = 'lifeodds-active-uid'

let _migrationInFlight = false

/** Sync local decisions to the server account, then merge any server-only decisions back.
 *
 * If the stored active UID differs from `userId` (different account on same device, or
 * no explicit logout before switching), we discard local decisions first and only pull
 * from the server. This prevents cross-account data contamination.
 */
async function migrateDecisions(userId: string): Promise<void> {
  if (_migrationInFlight) return
  _migrationInFlight = true
  try {
    const prevUid = localStorage.getItem(ACTIVE_UID_KEY)
    const isNewUser = prevUid !== null && prevUid !== userId

    if (isNewUser) {
      // Different account on same device — discard local state before syncing.
      useStore.setState({ decisions: [], results: {} })
    }

    const local = isNewUser ? [] : getLocalDecisions()
    const localIds = new Set(local.map((d) => d.id))
    const res = await syncDecisions(local)
    // Only stamp the UID after a successful sync — if sync throws, the wipe
    // above would be unrecoverable on the next login attempt.
    localStorage.setItem(ACTIVE_UID_KEY, userId)
    const newcomers = (res.server_decisions || []).filter((d) => !localIds.has(d.id))
    if (newcomers.length) {
      setLocalDecisions([...local, ...newcomers])
    }
    const totalSynced = res.imported_count + newcomers.length
    if (totalSynced > 0) {
      const parts: string[] = []
      if (res.imported_count > 0) {
        parts.push(`${res.imported_count} saved to your account`)
      }
      if (newcomers.length > 0) {
        parts.push(`${newcomers.length} pulled from another device`)
      }
      toast({
        kind: 'success',
        title: `Synced ${totalSynced} decision${totalSynced === 1 ? '' : 's'}`,
        message: parts.join(' · '),
      })
    }
  } catch {
    // best-effort; don't block login on sync failure
  } finally {
    _migrationInFlight = false
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined)
  const prevUidRef = useRef<string | null>(null)

  useEffect(() => {
    const timeout = setTimeout(() => setUser(null), 8000) // never hang forever
    authMe()
      .then((u) => { clearTimeout(timeout); setUser(u) })
      .catch(() => { clearTimeout(timeout); setUser(null) })
    return () => clearTimeout(timeout)
  }, [])

  // Initialize Apple JS SDK once on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && window.AppleID) {
      try {
        window.AppleID.auth.init({
          clientId: import.meta.env.VITE_APPLE_CLIENT_ID || 'app.lifeodds.web',
          scope: 'name email',
          redirectURI: window.location.origin + '/auth/apple/callback',
          usePopup: true,
        })
      } catch {
        // SDK may not be ready yet — signIn() will re-init implicitly
      }
    }
  }, [])

  // Identify / reset in PostHog whenever auth state changes.
  useEffect(() => {
    if (user) {
      // No PII (email/name) to analytics — the consent banner promises
      // "anonymous analytics", and PostHog/Umami only need a stable id plus
      // non-identifying cohort flags. role/is_premium are safe; email and name
      // are not, and sending them contradicts the stated privacy posture.
      analytics.identify(user.id, {
        role: user.role,
        is_premium: user.is_premium,
      })
    } else if (user === null) {
      analytics.reset()
    }
  }, [user])

  // Trigger migration on every transition from anon → authenticated.
  useEffect(() => {
    if (!user) {
      prevUidRef.current = null
      return
    }
    if (prevUidRef.current !== user.id) {
      prevUidRef.current = user.id
      migrateDecisions(user.id)
    }
  }, [user])

  const value: AuthCtx = {
    user,
    async login(email, password) {
      const u = await authLogin(email, password)
      setUser(u)
      analytics.track('user_logged_in', { method: 'email' })
    },
    async register(email, password, name) {
      const u = await authRegister(email, password, name)
      setUser(u)
      analytics.track('user_signed_up', { method: 'email' })
    },
    async logout() {
      await authLogout()
      setUser(null)
      useStore.setState({ decisions: [], results: {} })
      localStorage.removeItem(ACTIVE_UID_KEY)
    },
    async refresh() {
      try {
        const u = await authMe()
        setUser(u)
      } catch {
        setUser(null)
      }
    },
    async appleSignIn() {
      if (!window.AppleID) throw new Error('Apple Sign In not available')
      const result = await window.AppleID.auth.signIn()
      const idToken = result.authorization.id_token
      const n = result.user?.name
      const name = n ? [n.firstName, n.lastName].filter(Boolean).join(' ') || undefined : undefined
      const u = await authApple(idToken, name)
      setUser(u)
      analytics.track('user_signed_up', { method: 'apple' })
      // migrateDecisions fires automatically via the user watcher above
    },
    async googleSignIn(idToken: string) {
      const u = await authGoogle(idToken)
      setUser(u)
      analytics.track('user_signed_up', { method: 'google' })
    },
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth must be used inside AuthProvider')
  return v
}
